import { ChangeDetectorRef, Component, NgZone, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { IonIcon, IonSpinner } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { arrowBackOutline, keyOutline, mailOutline, shieldCheckmarkOutline } from 'ionicons/icons';
import { AuthService, MfaMethod } from '../../../services/auth.service';
import { InfoTooltipComponent } from '../../../components/info-tooltip/info-tooltip.component';

type VerificationMethod = 'totp' | 'email' | 'recovery';

@Component({
  selector: 'app-mfa-verify',
  standalone: true,
  imports: [FormsModule, RouterLink, IonIcon, IonSpinner, InfoTooltipComponent],
  templateUrl: './mfa-verify.component.html',
  styleUrls: ['./mfa-verify.component.scss']
})
export class MfaVerifyComponent implements OnInit {
  private readonly ngZone = inject(NgZone);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  challengeId = '';
  returnUrl = '/';
  methods: MfaMethod[] = [];
  selectedMethod: VerificationMethod = 'totp';
  code = '';
  isLoading = true;
  isSendingCode = false;
  emailCodeSent = false;
  errorMessage = '';
  infoMessage = '';

  constructor(
    private readonly authService: AuthService,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {
    addIcons({ arrowBackOutline, keyOutline, mailOutline, shieldCheckmarkOutline });
  }

  ngOnInit(): void {
    this.challengeId = this.route.snapshot.queryParamMap.get('challenge') || '';
    this.returnUrl = this.safeReturnUrl(this.route.snapshot.queryParamMap.get('returnUrl'));
    if (!this.challengeId) {
      void this.router.navigate(['/login']);
      return;
    }

    this.authService.getMfaChallenge(this.challengeId).subscribe({
      next: (challenge) => this.runInAngular(() => {
        this.methods = challenge.methods;
        this.selectedMethod = this.methods.some((method) => method.type === 'totp') ? 'totp' : 'email';
        this.isLoading = false;
      }),
      error: (error) => this.runInAngular(() => {
        this.errorMessage = this.getErrorMessage(error, 'This sign-in check has expired. Start again.');
        this.isLoading = false;
      })
    });
  }

  get hasEmailMethod(): boolean {
    return this.methods.some((method) => method.type === 'email');
  }

  get hasTotpMethod(): boolean {
    return this.methods.some((method) => method.type === 'totp');
  }

  chooseMethod(method: VerificationMethod): void {
    this.selectedMethod = method;
    this.code = '';
    this.errorMessage = '';
    this.infoMessage = '';
  }

  sendEmailCode(): void {
    if (this.isSendingCode) {
      return;
    }
    this.isSendingCode = true;
    this.errorMessage = '';
    this.authService.sendMfaEmailCode(this.challengeId).subscribe({
      next: (response) => this.runInAngular(() => {
        this.emailCodeSent = true;
        this.infoMessage = response.message;
        this.isSendingCode = false;
      }),
      error: (error) => this.runInAngular(() => {
        this.errorMessage = this.getErrorMessage(error, 'We could not send a code.');
        this.isSendingCode = false;
      })
    });
  }

  verify(): void {
    const normalizedCode = this.selectedMethod === 'recovery'
      ? this.code.trim()
      : this.code.replace(/\s/g, '');
    if (!normalizedCode || this.isLoading) {
      return;
    }
    this.isLoading = true;
    this.errorMessage = '';
    this.authService.verifyMfa(this.challengeId, this.selectedMethod, normalizedCode).subscribe({
      next: (response) => this.runInAngular(() => {
        this.isLoading = false;
        if (response.success) {
          void this.router.navigateByUrl(this.returnUrl);
        }
      }),
      error: (error) => this.runInAngular(() => {
        this.errorMessage = this.getErrorMessage(error, 'That code could not be verified.');
        this.isLoading = false;
      })
    });
  }

  private safeReturnUrl(value: string | null): string {
    return value && value.startsWith('/') && !value.startsWith('//') ? value : '/';
  }

  private getErrorMessage(error: unknown, fallback: string): string {
    const candidate = (error as { error?: { error?: { message?: unknown }; message?: unknown } })?.error;
    const message = candidate?.error?.message ?? candidate?.message;
    return typeof message === 'string' && message.trim() ? message : fallback;
  }

  private runInAngular(callback: () => void): void {
    this.ngZone.run(() => {
      callback();
      this.changeDetectorRef.detectChanges();
    });
  }
}
